interface MetaConfig {
  title: string;
  description: string;
  keywords?: string;
  ogTitle?: string;
  ogDescription?: string;
  ogImage?: string;
  canonical?: string;
}

export const metaConfigs: Record<string, MetaConfig> = {
  '/': {
    title: 'ProptPilot - Smart Property Management Dashboard',
    description: 'Streamline your property portfolio with ProptPilot. Track properties, manage finances, automate maintenance requests, and optimize your rental income all in one place.',
    keywords: 'property management, rental properties, landlord software, property portfolio, BRRR calculator, property investment, rental income tracking',
  },
  '/landing': {
    title: 'ProptPilot - Smart Property Management for Modern Landlords',
    description: 'Professional property management software for landlords and investors. Track properties, automate finances, manage compliance, and optimize rental income with our comprehensive platform.',
    keywords: 'property management software, landlord tools, rental property tracking, property investment, BRRR strategy, rental income optimization',
  },
  '/about': {
    title: 'About ProptPilot - Property Management Made Simple',
    description: 'Learn about ProptPilot\'s mission to simplify property management for landlords and investors worldwide. Discover our comprehensive tools and features.',
    keywords: 'about proptpilot, property management company, landlord solutions, property investment tools',
  },
  '/contact': {
    title: 'Contact ProptPilot - Get Support & Expert Advice',
    description: 'Get in touch with ProptPilot for support, questions, or expert advice on property management. Our team is here to help optimize your property portfolio.',
    keywords: 'contact proptpilot, property management support, landlord help, customer service',
  },
  '/login': {
    title: 'Login to ProptPilot - Access Your Property Dashboard',
    description: 'Secure login to your ProptPilot property management dashboard. Access your portfolio, track finances, and manage your rental properties.',
    keywords: 'proptpilot login, property dashboard access, landlord portal, secure login',
  },
  '/signup': {
    title: 'Sign Up for ProptPilot - Start Managing Properties Today',
    description: 'Create your free ProptPilot account and start managing your property portfolio professionally. Join thousands of successful landlords and investors.',
    keywords: 'proptpilot signup, property management registration, free landlord account, property portfolio tracking',
  },
  '/dashboard': {
    title: 'Property Dashboard - ProptPilot',
    description: 'Your comprehensive property management dashboard. View portfolio performance, track finances, monitor maintenance requests, and get insights on your rental properties.',
    keywords: 'property dashboard, landlord dashboard, rental property overview, portfolio management',
  },
  '/properties': {
    title: 'My Properties - Property Portfolio Management | ProptPilot',
    description: 'Manage your entire property portfolio in one place. View property details, track performance, monitor maintenance, and optimize rental income.',
    keywords: 'property portfolio, manage properties, rental properties, property tracking, landlord properties',
  },
  '/finance': {
    title: 'Financial Management - Track Property Income & Expenses | ProptPilot',
    description: 'Comprehensive financial tracking for your property portfolio. Monitor rental income, track expenses, analyze cash flow, and generate financial reports.',
    keywords: 'property finance, rental income tracking, property expenses, cash flow analysis, financial reports',
  },
  '/brrr-projects': {
    title: 'BRRR Projects - Buy, Refurbish, Rent, Refinance (BRRR) | ProptPilot',
    description: 'Plan and track your BRRR (Buy, Refurbish, Rent, Refinance) property investment projects. Calculate returns, manage refurbishments, and optimize your strategy.',
    keywords: 'BRRR calculator, property investment strategy, buy refurbish rent refinance, property flipping, investment analysis',
  },
  '/investment-analyser': {
    title: 'Investment Analyser - Property Deal Calculator | ProptPilot',
    description: 'Analyze property investment opportunities with our comprehensive calculator. Evaluate rental yield, cash flow, ROI, and make informed investment decisions.',
    keywords: 'property investment calculator, rental yield calculator, property ROI, investment analysis, deal analyzer',
  },
  '/rent-trends': {
    title: 'Rent Trends & Market Analysis - Property Market Data | ProptPilot',
    description: 'Access comprehensive rent trends and market analysis for informed property investment decisions. Track local market conditions and rental rates.',
    keywords: 'rent trends, property market analysis, rental rates, market data, property valuation',
  },
  '/compliance': {
    title: 'Property Compliance Management - Gas Safety & EPC Tracking | ProptPilot',
    description: 'Stay compliant with automated gas safety certificate tracking, EPC renewals, and property inspection reminders. Never miss a compliance deadline again.',
    keywords: 'property compliance, gas safety certificates, EPC tracking, CP12 renewals, property inspections, landlord compliance',
    ogTitle: 'Property Compliance Management - Stay Compliant & Avoid Fines',
    ogDescription: 'Automate gas safety certificates, EPC renewals, and property inspections with smart tracking and automated reminders.',
  },
  '/bank-reconciliation': {
    title: 'Automatic Rent Tracking & Bank Reconciliation for Landlords | ProptPilot',
    description: 'Automate rent payment tracking and bank reconciliation. Connect your bank account to automatically match payments and catch missing rent instantly.',
    keywords: 'bank reconciliation, rent tracking, automatic rent matching, property finance, rental income tracking, landlord banking',
    ogTitle: 'Automatic Rent Tracking & Bank Reconciliation',
    ogDescription: 'Connect your bank account and automatically match rent payments to properties. Never chase missing payments again.',
  },
  '/manage-maintenance-team-tasks': {
    title: 'Property Maintenance & Team Management Software | ProptPilot',
    description: 'Streamline maintenance requests, manage contractor networks, and coordinate team tasks. Keep tenants happy and properties well-maintained.',
    keywords: 'property maintenance management, contractor network, team task management, maintenance requests, property repairs',
    ogTitle: 'Property Maintenance & Team Management Software',
    ogDescription: 'Centralize maintenance requests, manage contractor networks, and track team tasks in one powerful platform.',
  },
  '/references': {
    title: 'Tenant Reference Checks - Reliable Tenant Screening | ProptPilot',
    description: 'Comprehensive tenant reference checking and screening services. Verify tenant backgrounds, credit history, and rental references for peace of mind.',
    keywords: 'tenant reference checks, tenant screening, credit checks, rental references, tenant verification',
  },
  '/help': {
    title: 'Help Centre - ProptPilot Support & Guides',
    description: 'Find answers to common questions, access user guides, and get support for your ProptPilot property management platform.',
    keywords: 'proptpilot help, property management support, user guides, faq, customer support',
  },
  '/terms': {
    title: 'Terms of Service - ProptPilot',
    description: 'Read ProptPilot\'s terms of service and user agreement for our property management platform and services.',
    keywords: 'terms of service, user agreement, proptpilot terms, legal terms',
  },
  '/privacy': {
    title: 'Privacy Policy - ProptPilot',
    description: 'ProptPilot\'s privacy policy outlines how we protect and handle your personal data and property information.',
    keywords: 'privacy policy, data protection, personal information, proptpilot privacy',
  },
  '/tenant/login': {
    title: 'Tenant Portal Login - Access Your Rental Account | ProptPilot',
    description: 'Secure tenant portal login. Access your rental account, submit maintenance requests, view lease information, and communicate with your landlord.',
    keywords: 'tenant portal, tenant login, rental account, tenant services, maintenance requests',
  },
  // New development project routes
  '/development-projects': {
    title: 'Development Projects - Construction Project Management | ProptPilot',
    description: 'Manage your property development projects from planning to completion. Track phases, budgets, contractors, and timelines in one comprehensive platform.',
    keywords: 'development projects, construction management, property development, project tracking, building projects',
  },
  '/development-projects/new': {
    title: 'New Development Project - Create Construction Project | ProptPilot',
    description: 'Start a new property development project. Set up phases, budgets, timelines, and contractor assignments for successful project delivery.',
    keywords: 'new development project, create construction project, property development planning, project setup',
  },
  '/new-project': {
    title: 'New Project - Create Development or BRRR Project | ProptPilot',
    description: 'Create a new property investment or development project. Choose between BRRR strategy or full development project management.',
    keywords: 'new property project, BRRR project, development project, property investment planning',
  },
  // Area Intelligence routes
  '/area-intelligence': {
    title: 'Area Intelligence - Local Property Market Data | ProptPilot',
    description: 'Get comprehensive area intelligence including crime rates, school ratings, transport links, and local amenities to make informed property investment decisions.',
    keywords: 'area intelligence, local market data, property research, neighborhood analysis, investment location',
  },
  // Property Value Search routes
  '/property-value-search': {
    title: 'Property Value Search - Instant Property Valuations | ProptPilot',
    description: 'Search and compare property values instantly. Get accurate valuations, comparable sales, and market trends for any UK property.',
    keywords: 'property value search, property valuation, house prices, property comparison, market value',
  },
  // Rent Reconciliation routes
  '/rent-reconciliation': {
    title: 'Rent Reconciliation - Automatic Rent Matching | ProptPilot',
    description: 'Automatically reconcile rent payments with bank transactions. Track missing payments, identify discrepancies, and streamline rent collection.',
    keywords: 'rent reconciliation, rent tracking, payment matching, rent collection, automatic reconciliation',
  },
  // Maintenance routes
  '/maintenance': {
    title: 'Maintenance Management - Property Repairs & Requests | ProptPilot',
    description: 'Manage property maintenance requests, track repairs, assign contractors, and ensure tenant satisfaction with our comprehensive maintenance system.',
    keywords: 'property maintenance, repair management, maintenance requests, contractor management, property repairs',
  },
  '/maintenance/new': {
    title: 'New Maintenance Request - Report Property Issue | ProptPilot',
    description: 'Submit a new maintenance request for property repairs. Upload photos, describe the issue, and track progress from submission to completion.',
    keywords: 'new maintenance request, report repair, property issue, maintenance submission',
  },
  // Tenant Portal routes
  '/tenant-portal': {
    title: 'Tenant Portal - Tenant Services & Information | ProptPilot',
    description: 'Access tenant services including maintenance requests, rent payments, lease information, and communication with your landlord.',
    keywords: 'tenant portal, tenant services, rent payment, maintenance requests, tenant dashboard',
  },
  '/tenant-portal/maintenance': {
    title: 'Tenant Maintenance - Submit Repair Requests | ProptPilot',
    description: 'Submit and track maintenance requests for your rental property. Upload photos, describe issues, and monitor repair progress.',
    keywords: 'tenant maintenance, repair requests, property issues, tenant repairs',
  },
  '/tenant-portal/payments': {
    title: 'Rent Payments - Pay Rent Online | ProptPilot',
    description: 'Pay your rent securely online. View payment history, upcoming due dates, and manage your rental payments easily.',
    keywords: 'rent payment, pay rent online, tenant payments, rental payments, secure payment',
  },
  // New high-intent landing pages
  '/brrr-deal-calculator-uk': {
    title: 'BRRR Deal Calculator UK - Track Property Investment ROI | ProptPilot',
    description: 'Track your BRRR projects from purchase to refinance. Calculate ROI instantly with our comprehensive BRRR deal calculator for UK property investors.',
    keywords: 'BRRR calculator UK, property investment ROI, buy refurb rent refinance, property deal analyzer, UK property investment',
    ogTitle: 'BRRR Deal Calculator UK - Property Investment ROI Tracker',
    ogDescription: 'Professional BRRR calculator for UK property investors. Track projects from purchase to refinance and calculate ROI instantly.',
  },
  '/property-management-tool-for-landlords': {
    title: 'Property Management Tool for Landlords UK - Rent & Tenant Management | ProptPilot',
    description: 'Manage rent, tenants, maintenance, and compliance in one place. Professional property management software for UK landlords.',
    keywords: 'property management tool UK, landlord software, tenant management, rent collection, property compliance',
    ogTitle: 'Property Management Tool for UK Landlords',
    ogDescription: 'Complete property management solution for landlords. Manage rent, tenants, maintenance, and compliance efficiently.',
  },
  '/tools-for-airbnb-investors': {
    title: 'Airbnb Tools for Hosts & Serviced Accommodation | ProptPilot',
    description: 'Manage serviced accommodation, track income, and automate tasks. Professional tools for Airbnb hosts and short-let investors.',
    keywords: 'Airbnb management tools, serviced accommodation software, short let management, Airbnb income tracker',
    ogTitle: 'Professional Airbnb Management Tools for Hosts',
    ogDescription: 'Streamline your Airbnb business with professional management tools. Track income, manage bookings, and automate tasks.',
  },
  '/refinance-tracker-for-real-estate': {
    title: 'Refinance Tracker Tool - Property Investment Exit Planning | ProptPilot',
    description: 'Plan your refinance exit and project cash-out ROI confidently. Track refinance opportunities and optimize your property portfolio.',
    keywords: 'refinance tracker, property refinance calculator, real estate exit planning, cash out refinance, property portfolio optimization',
    ogTitle: 'Property Refinance Tracker & Exit Planning Tool',
    ogDescription: 'Plan your property refinance strategy with confidence. Track opportunities and calculate cash-out ROI for optimal exits.',
  },
  // Blog routes
  '/blog': {
    title: 'Property Investment Blog - Expert Tips & Insights | ProptPilot',
    description: 'Discover expert property investment tips, BRRR strategies, compliance guides, and property management insights. Learn from successful landlords and investors.',
    keywords: 'property investment blog, BRRR strategy, property management tips, rental property advice, UK property investment, landlord insights',
    ogTitle: 'Property Investment Blog - Expert Tips & Insights',
    ogDescription: 'Discover expert property investment tips, BRRR strategies, compliance guides, and property management insights.',
  },
  '/resources': {
    title: 'Property Investment Resources - Guides & Tools | ProptPilot',
    description: 'Access comprehensive property investment resources, guides, and tools to help you build and manage your rental property portfolio successfully.',
    keywords: 'property investment resources, rental property guides, landlord tools, property management resources',
    ogTitle: 'Property Investment Resources - Guides & Tools',
    ogDescription: 'Access comprehensive property investment resources, guides, and tools for successful rental property management.',
  },
};

export const getMetaConfig = (pathname: string): MetaConfig => {
  return metaConfigs[pathname] || metaConfigs['/'];
};
